"use client";

import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Target, CheckCircle2, XCircle, TrendingUp, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { useState } from "react";

const accuracyData = [
  {
    id: 1,
    title: "Philosophy Essay - Ethical Arguments",
    subject: "Philosophy",
    university: "University of Oxford",
    teacherMark: 74,
    aiMark: 71,
    difference: -1,
    accurate: true,
    originalImage: "/results/resultsorignal1.jpeg",
    reportImage: "/results/result1system.png",
    feedback: "AI assessment closely matched professor's evaluation of argument structure and critical analysis.",
  },
  {
    id: 2,
    title: "Computer Science - Algorithm Design",
    subject: "Computer Science",
    university: "Imperial College London",
    teacherMark: 58,
    aiMark: 61,
    difference: +3,
    accurate: true,
    originalImage: "/results/result2origin.png",
    reportImage: "/results/result2system.png",
    feedback: "Near-perfect alignment in code efficiency and algorithmic complexity assessment.",
  },
  {
    id: 3,
    title: "Biology Lab Report - Cell Biology",
    subject: "Biology",
    university: "University of Cambridge",
    teacherMark: 64,
    aiMark: 65,
    difference: +1,
    accurate: true,
    originalImage: "/results/result3original.png",
    reportImage: "/results/result3system.png",
    feedback: "AI detected strong methodology that aligned with professor's grading criteria.",
  },
  {
    id: 4,
    title: "Business Strategy - Market Analysis",
    subject: "Business",
    university: "London School of Economics",
    teacherMark: 68,
    aiMark: 64,
    difference: -4,
    accurate: true,
    originalImage: "/results/result4original.png",
    reportImage: "/results/image4system.png",
    feedback: "Consistent evaluation of data analysis and strategic recommendations.",
  },
  {
    id: 5,
    title: "English Literature - Victorian Poetry",
    subject: "English",
    university: "University of Edinburgh",
    teacherMark: 65,
    aiMark: 64,
    difference: -1,
    accurate: true,
    originalImage: "/results/result5original.png",
    reportImage: "/results/result5system.png",
    feedback: "AI accurately assessed literary analysis depth and critical interpretation.",
  }
];

const accurateCount = accuracyData.filter(item => item.accurate).length;
const totalCount = accuracyData.length;
const accuracyRate = Math.round((accurateCount / totalCount) * 100);

export default function ResultsContent() {
  const [activeView, setActiveView] = useState<{ [key: number]: 'original' | 'report' }>({});

  const toggleView = (id: number, view: 'original' | 'report') => {
    setActiveView(prev => ({ ...prev, [id]: view }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-background">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto px-4 py-8"
      >
        <div className="flex items-center justify-between mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6"
          >
            <Target className="h-8 w-8 text-primary" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
          >
            AssignOwl Grading Accuracy — Validated Against UK University Professors
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
          >
            Real comparison between professor marks and our AI scoring system across Oxford, Cambridge, LSE, Imperial College London, and University of Edinburgh
          </motion.p>
        </div>
      </motion.div>

      {/* Results Grid */}
      <div className="container mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
          {accuracyData.map((result, index) => (
            <motion.div
              key={result.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * (index % 4) }}
              className="group"
            >
              <div className={`p-6 rounded-2xl bg-card/50 backdrop-blur-sm border transition-all duration-300 ${result.accurate
                ? 'border-green-500/20 hover:border-green-500/40'
                : 'border-yellow-500/20 hover:border-yellow-500/40'
                }`}>
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-foreground mb-1">
                      {result.title}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {result.subject} • {result.university}
                    </p>
                  </div>
                  <div className="shrink-0 ml-3">
                    {result.accurate ? (
                      <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-xs font-medium text-green-500">Accurate</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20">
                        <XCircle className="h-4 w-4 text-yellow-500" />
                        <span className="text-xs font-medium text-yellow-500">Variance</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Image with Toggle */}
                <div className="relative mb-4 rounded-xl overflow-hidden">
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex gap-2 bg-black/50 backdrop-blur-sm rounded-lg p-1">
                    <Button
                      size="sm"
                      variant={(!activeView[result.id] || activeView[result.id] === 'original') ? "default" : "ghost"}
                      onClick={() => toggleView(result.id, 'original')}
                      className="text-xs h-8"
                    >
                      Original Marks
                    </Button>
                    <Button
                      size="sm"
                      variant={activeView[result.id] === 'report' ? "default" : "ghost"}
                      onClick={() => toggleView(result.id, 'report')}
                      className="text-xs h-8"
                    >
                      Our System
                    </Button>
                  </div>

                  <div className="aspect-[16/9] relative">
                    <motion.div
                      initial={false}
                      animate={{
                        opacity: (!activeView[result.id] || activeView[result.id] === 'original') ? 1 : 0,
                        scale: (!activeView[result.id] || activeView[result.id] === 'original') ? 1 : 0.95
                      }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0"
                    >
                      <img
                        src={result.originalImage}
                        alt={`Original professor marks for ${result.title} at ${result.university}`}
                        className="object-contain w-full h-full"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <div className="text-xs font-medium text-white/80 mb-1">Teacher&apos;s Score</div>
                        <div className="text-3xl font-bold text-white">{result.teacherMark}%</div>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={false}
                      animate={{
                        opacity: activeView[result.id] === 'report' ? 1 : 0,
                        scale: activeView[result.id] === 'report' ? 1 : 0.95
                      }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0"
                    >
                      <img
                        src={result.reportImage}
                        alt={`AssignOwl AI prediction for ${result.title}`}
                        className="object-contain w-full h-full"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <div className="text-xs font-medium text-white/80 mb-1">Our Prediction</div>
                        <div className="text-3xl font-bold text-white">{result.aiMark}%</div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Score Comparison */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 text-center">
                    <div className="text-xs text-muted-foreground mb-1">Our Prediction</div>
                    <div className="text-2xl font-bold text-primary">{result.aiMark}%</div>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/20 border border-white/[0.05] text-center">
                    <div className="text-xs text-muted-foreground mb-1">Actual Score</div>
                    <div className="text-2xl font-bold text-foreground">{result.teacherMark}%</div>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/20 border border-white/[0.05] text-center">
                    <div className="text-xs text-muted-foreground mb-1">Difference</div>
                    <div className={`text-2xl font-bold ${Math.abs(result.difference) <= 2 ? 'text-green-500' : 'text-yellow-500'}`}>
                      {result.difference > 0 ? '+' : ''}{result.difference}%
                    </div>
                  </div>
                </div>

                {/* Feedback */}
                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/10">
                  <TrendingUp className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    {result.feedback}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="max-w-4xl mx-auto mt-16"
        >
          <div className="p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-white/[0.1]">
            <h2 className="text-2xl font-bold text-foreground mb-4 text-center">
              {accuracyRate}% Accuracy Rate
            </h2>
            <p className="text-muted-foreground text-center mb-6">
              Our scoring system achieves accurate predictions
              (within ±10% variance) when compared to actual professor marks across top universities.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <h3 className="font-semibold text-foreground">Accurate Predictions</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  All assignments scored within ±10% of professor marks, demonstrating reliable assessment accuracy.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <TriangleAlert className="h-5 w-5 text-yellow-500" />
                  <h3 className="font-semibold text-foreground">Smart Variances</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  All assignments showed slight variance, highlighting areas for continuous improvement.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1 }}
          className="max-w-4xl mx-auto mt-12 text-center"
        >
          <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 border border-white/[0.1]">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Experience Accurate Grading
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Join students across universities using AssignOwl for reliable assignment feedback before submission.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/scorer">
                <Button size="lg" className="gap-2">
                  Try it now
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link href="/contact">
                <Button size="lg" variant="outline">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
